export type FieldId =
  | "caseIdOrTrackingNumber"
  | "healthPlan"
  | "assignee"
  | "caseType"
  | "caseStatus"
  | "outcome"
  | "workRequested"
  | "workCompleted"
  | "delegatedVendor"
  | "memberName"
  | "memberId"
  | "memberDob";

export type FilterType = "MultiSelect" | "SingleSelect" | "SearchableMultiSelect" | "DatePicker" | "TextField";

export type Section = "CASE_INFORMATION" | "MEMBER_INFORMATION";

export interface UserCtx {
  hasCrossClientAccess: boolean;
  hasDelegatedVendor: boolean;
  isSupervisor: boolean;
  roles: string[];
  opsGroup?: string;
}

export type FormValues = Record<string, unknown>;

export type ReferenceDataHookKey =
  | "healthPlanOptions"
  | "delegatedVendorOptions"
  | "caseTypeOptions"
  | "caseStatusOptions"
  | "outcomeOptions";

export type FieldOptions = { static: Array<{ id: string; label?: string }> } | { hook: ReferenceDataHookKey };

export interface FieldSpec {
  id: FieldId;
  section: Section;
  label: string;
  filterType: FilterType;
  apiParamName: string;
  visibleWhen?: (v: FormValues, ctx: UserCtx) => boolean;
  disabledWhen?: (v: FormValues, ctx: UserCtx) => boolean;
  /** Also disable the field when it resolves to zero options (e.g. a health plan with no vendors). */
  disabledWhenNoOptions?: boolean;
  disabledTooltip?: string;
  options?: FieldOptions;
  resetWhen?: FieldId[];
  multiSelect?: boolean;
  maxMenuHeight?: number | string;
}

const CASE_STATUS_OPEN = "OPEN";

export const MEMBER_FIELDS: FieldId[] = ["memberName", "memberId", "memberDob"];

export function isMemberValueFilled(value: unknown): boolean {
  if (Array.isArray(value)) {
    return (value as string[]).some((s) => Boolean(s?.trim()));
  }
  return Boolean(value);
}

export const FIELD_REGISTRY: FieldSpec[] = [
  // -- CASE INFORMATION --

  {
    id: "caseIdOrTrackingNumber",
    section: "CASE_INFORMATION",
    label: "Case ID or Tracking #",
    filterType: "TextField",
    apiParamName: "caseIdOrTrackingNumber",
  },
  {
    id: "healthPlan",
    section: "CASE_INFORMATION",
    label: "Health plan",
    filterType: "SingleSelect",
    apiParamName: "healthPlan",
    visibleWhen: (_v, ctx) => ctx.hasCrossClientAccess,
    options: { hook: "healthPlanOptions" },
  },
  {
    id: "delegatedVendor",
    section: "CASE_INFORMATION",
    label: "Delegated vendor",
    filterType: "SingleSelect",
    apiParamName: "delegatedVendor",
    visibleWhen: (_v, ctx) => ctx.hasDelegatedVendor,
    disabledWhen: (v) => !v.healthPlan,
    disabledWhenNoOptions: true,
    disabledTooltip: "Select a health plan to choose a delegated vendor.",
    options: { hook: "delegatedVendorOptions" },
    resetWhen: ["healthPlan"],
  },
  {
    id: "caseType",
    section: "CASE_INFORMATION",
    label: "Case type",
    filterType: "MultiSelect",
    apiParamName: "caseTypeList",
    multiSelect: true,
    options: { hook: "caseTypeOptions" },
    maxMenuHeight: 300,
  },
  {
    id: "caseStatus",
    section: "CASE_INFORMATION",
    label: "Case status",
    filterType: "MultiSelect",
    apiParamName: "caseStatusList",
    multiSelect: true,
    options: { hook: "caseStatusOptions" },
    disabledWhen: (v) => MEMBER_FIELDS.some((f) => isMemberValueFilled(v[f])),
    disabledTooltip: "Member search only applies to completed cases.",
  },
  {
    id: "outcome",
    section: "CASE_INFORMATION",
    label: "Outcome",
    filterType: "SearchableMultiSelect",
    apiParamName: "outcomeList",
    multiSelect: true,
    disabledWhen: (v) => {
      const statuses = v.caseStatus as string[] | undefined;
      return Boolean(statuses?.includes(CASE_STATUS_OPEN));
    },
    disabledTooltip: "When Case Status is 'Open', cases do not have an Outcome.",
    options: { hook: "outcomeOptions" },
    resetWhen: ["caseStatus"],
  },
  {
    id: "assignee",
    section: "CASE_INFORMATION",
    label: "Assignee",
    filterType: "SearchableMultiSelect",
    apiParamName: "assigneeNameList",
    multiSelect: true,
    visibleWhen: (_v, ctx) => ctx.isSupervisor,
  },
  {
    id: "workRequested",
    section: "CASE_INFORMATION",
    label: "Work requested",
    filterType: "DatePicker",
    apiParamName: "dateCreatedAfter",
  },
  {
    id: "workCompleted",
    section: "CASE_INFORMATION",
    label: "Work completed",
    filterType: "DatePicker",
    apiParamName: "caseCompletedBefore",
  },

  // -- MEMBER INFORMATION --

  {
    id: "memberName",
    section: "MEMBER_INFORMATION",
    label: "Member name",
    filterType: "TextField",
    apiParamName: "memberName",
  },
  {
    id: "memberId",
    section: "MEMBER_INFORMATION",
    label: "Member ID",
    filterType: "TextField",
    apiParamName: "memberId",
  },
  {
    id: "memberDob",
    section: "MEMBER_INFORMATION",
    label: "Member DOB",
    filterType: "DatePicker",
    apiParamName: "memberDob",
  },
];
